<template>
  <div class="current-month-cashflow-view">
    <div class="traffic-light-line">
      <div class="labeled-line">
        <div class="label" :class="{ positive: hasRemainingVariables, zero: !hasRemainingVariables, negative: !isPredictedCashflowPositive }">
          {{ fullMonthName }}
        </div>
        <div class="line" :class="{ positive: hasRemainingVariables, zero: !hasRemainingVariables, negative: !isPredictedCashflowPositive }"></div>
        <div class="circle" :class="{ positive: hasRemainingVariables, zero: !hasRemainingVariables, negative: !isPredictedCashflowPositive }"></div>
        <img class="triangle"
             v-if="!isLastCashflow"
             v-on:click="changeBudgetMonth()"
             :src="require('@/assets/icons/right-triangle.svg')">
      </div>
    </div>
    <div class="current-cashflow-view-container">
      <weekly-variables-summary v-if="isPredictedCashflowPositive"/>
      <expected-end-of-month v-else/>
      <riseup-goal/>
      <cashflow-type-weekly-variables/>
      <cashflow-type-economy/>
      <transition-group name="list">
        <div v-for="subtree in trackingCategories" :key="subtree.categoryId">
          <cashflow-type-tracking-category :subtree=subtree />
        </div>
        <tracking-category-creation-section :key="'create-section'"/>
      </transition-group>
      <cashflow-type-fixed-expense/>
      <cashflow-type-income/>
    </div>
    <div class="current-cashflow-view-container">
      <hr class="divider" v-if="this.hasExcluded" />
      <cashflow-type-excluded v-if="this.hasExcluded"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import Segment from '../../../../../../../Segment';
import ExpectedEndOfMonth from './components/ExpectedEndOfMonth.vue';
import RiseupGoal from './components/RiseupGoal.vue';
import CashflowTypeIncome from './components/types/CashflowTypeIncome.vue';
import CashflowTypeExcluded from './components/types/CashflowTypeExcluded.vue';
import CashflowTypeEconomy from './components/types/CashflowTypeEconomy.vue';
import CashflowTypeFixedExpense from './components/types/CashflowTypeFixedExpense.vue';
import CashflowTypeWeeklyVariables from './components/types/CashflowTypeWeeklyVariables.vue';
import WeeklyVariablesSummary from './components/WeeklyVariablesSummary.vue';
import cashflowViewConsts from '../../../../../../../constants/cashflow-view';
import CashflowTypeTrackingCategory from './components/types/CashflowTypeTrackingCategory.vue';
import TrackingCategoryCreationSection from './components/cashflow-edit/edit-reccuring/TrackingCategoryCreationSection.vue';

export default {
  name: 'CurrentMonthCashflowView',
  components: {
    CashflowTypeTrackingCategory,
    WeeklyVariablesSummary,
    CashflowTypeWeeklyVariables,
    RiseupGoal,
    ExpectedEndOfMonth,
    CashflowTypeIncome,
    CashflowTypeExcluded,
    CashflowTypeEconomy,
    CashflowTypeFixedExpense,
    TrackingCategoryCreationSection,
  },
  created() {
    Segment.trackUserGot('CurrentCashflowViewEntered');
  },
  computed: {
    ...mapState('cashflowView', ['cashflowUITree', 'presentedBudgets', 'presentedBudgetDate']),
    ...mapGetters('cashflowView', ['fullMonthName', 'predictedBalanceAtEndOfMonth', 'remainingVariables', 'hasExcluded']),
    isPredictedCashflowPositive() {
      return this.predictedBalanceAtEndOfMonth > 0;
    },
    isLastCashflow() {
      const lastBudget = _.last(this.presentedBudgets);
      return lastBudget && this.presentedBudgetDate === lastBudget.budgetDate;
    },
    hasRemainingVariables() {
      return this.remainingVariables > 0;
    },
    trackingCategories() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory];
    },
  },
  methods: {
    ...mapActions('cashflowView', ['moveToNextBudgetMonth']),
    async changeBudgetMonth() {
      await this.moveToNextBudgetMonth();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../scss/mixins';

  @keyframes shake {
    0% { transform: translate(2px, 0); }
    50% { transform: translate(-2px, 0); }
    100% { transform: translate(2px, 0); }
  }

  .current-month-cashflow-view {
    padding-bottom: 80px;

    @media (min-width: 430px) {
      padding-bottom: 20px;
    }

    .traffic-light-line {
      @include responsive-padding;
      width: 100%;
      text-align: center;
      margin-bottom: 11px;

      .labeled-line {
        width: 100%;
        padding-top: 12px;
        position: relative;
        display: inline-block;

        .positive {
          background-color: $riseup_green;
        }

        .zero {
          background-color: $riseup_mustard;
        }

        .negative {
          background-color: $riseup_coral;
        }

        .label {
          height: 18px;
          line-height: 15px;
          font-size: 14px;
          padding: 2px 4px 2px 4px;
          display: inline-block;
          color: $riseup_white;
          font-weight: bold;
          position: absolute;
          right: 0;
          top: -3px;
        }

        .line {
          height: 3px;
        }

        .circle {
          position: absolute;
          left: 0;
          top: 5px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: inline-block;
        }

        .triangle {
          position: absolute;
          animation: shake 0.5s;
          animation-iteration-count: 5;
          right: -20px;
          top: -4px;
        }
      }
    }
    .current-cashflow-view-container {
      @include responsive-padding;
      font-family: SimplerPro;
      text-align: right;
      width: 100%;
      margin: 0 auto;

      .divider {
        color: $riseup_gray_2;
        opacity: 0.5;
        margin: 25px 0;

        &.tight {
          margin-bottom: 0;
        }
      }

      .list-enter-active, .list-leave-active {
        transition: all 1s;
      }
      .list-enter, .list-leave-to {
        opacity: 0;
      }
      .list-enter-to {
        opacity: 1;
      }
    }
  }
</style>
